import React, { useEffect, useRef, useMemo, useState } from 'react';
import { Chart } from '@antv/g2';
import { ConfigProvider } from 'antd';
import { useTheme } from '../../theme/ThemeContext';
import Select from '../../atoms/Select/Select';
import Button from '../../atoms/Button/Button';
import DownloadFile from '../../atoms/Icon/DownloadFile';
import ArrowRight from '../../atoms/Icon/ArrowRight';
import { WaterfallChartProps, ProcessedWaterfallData, WaterfallDimension } from './WaterfallChart.types';
import {
    getWaterfallSelectionResetKey,
    processWaterfallData,
    resolveWaterfallDefaultDimension,
    resolveWaterfallDimensionOptions,
    resolveWaterfallMetricOptions,
    resolveWaterfallYAxisTitle,
} from './waterfallDataUtils';
import { CHART_FONT_FAMILY_CSS, formatFixedSpacingXAxisLabel } from '../../../utils/formatters';

const WATERFALL_BAND_PADDING_INNER = 0.3;
const WATERFALL_BAND_PADDING_OUTER = 0.2;
const WATERFALL_X_AXIS_SIZE = 100;
const WATERFALL_Y_AXIS_RESERVE_PX = 72;
const WATERFALL_CHART_HORIZONTAL_PADDING_PX = 32;

const WaterfallChart: React.FC<WaterfallChartProps> = (props) => {
    const {
        config,
        title,
        description,
        filters = [],
        footerLabel,
        showFooterArrow = true,
        onDownload,
        onBarClick,
        height = 400,
        positiveColor = '#52C41A',
        negativeColor = '#FF4D4F',
        totalColor = '#5B50D1',
        connectorColor = 'rgba(0, 0, 0, 0.25)',
        className = '',
        chartClassName = '',
        showLabels = true,
        showConnectors = true,
        showPercentages = true,
    } = props;

    const theme = useTheme();
    const chartContainer = useRef<HTMLDivElement | null>(null);
    const chartInstance = useRef<Chart | null>(null);
    const [chartWrapperWidth, setChartWrapperWidth] = useState(0);

    const { viz_data } = config;
    const chartTitle = title || viz_data.chartTitle;

    const metricOptions = useMemo(() => resolveWaterfallMetricOptions(viz_data), [viz_data]);
    const dimensionOptions = useMemo(() => resolveWaterfallDimensionOptions(viz_data), [viz_data]);
    const showMetricDropdown = metricOptions.length > 1;
    const showDimensionDropdown = dimensionOptions.length > 1;

    const [selectedMetricIndex, setSelectedMetricIndex] = useState(metricOptions[0]?.value ?? 0);
    const [selectedDimension, setSelectedDimension] = useState<WaterfallDimension>(() =>
        resolveWaterfallDefaultDimension(viz_data, dimensionOptions),
    );

    const selectionResetKey = getWaterfallSelectionResetKey(viz_data);

    useEffect(() => {
        setSelectedMetricIndex(metricOptions[0]?.value ?? 0);
        setSelectedDimension(resolveWaterfallDefaultDimension(viz_data, dimensionOptions));
        // Reset only when chart payload content changes, not on viz_data reference churn.
        // eslint-disable-next-line react-hooks/exhaustive-deps -- metric/dimension options are derived from selectionResetKey
    }, [selectionResetKey]);

    const yAxisTitle = useMemo(
        () => resolveWaterfallYAxisTitle(viz_data, selectedMetricIndex),
        [viz_data, selectedMetricIndex],
    );

    const processedData = useMemo(
        (): ProcessedWaterfallData[] =>
            processWaterfallData(viz_data, selectedMetricIndex, selectedDimension),
        [viz_data, selectedMetricIndex, selectedDimension],
    );

    useEffect(() => {
        const wrapper = chartContainer.current;
        if (!wrapper) return;

        const updateWidth = () => {
            setChartWrapperWidth(wrapper.getBoundingClientRect().width);
        };

        updateWidth();
        const resizeObserver = new ResizeObserver(updateWidth);
        resizeObserver.observe(wrapper);

        return () => {
            resizeObserver.disconnect();
        };
    }, []);

    const xAxisLabelStridePx = useMemo(() => {
        const categoryCount = Math.max(processedData.length, 1);
        const plotWidth = Math.max(
            80,
            chartWrapperWidth - WATERFALL_Y_AXIS_RESERVE_PX - WATERFALL_CHART_HORIZONTAL_PADDING_PX,
        );

        return plotWidth / (categoryCount + WATERFALL_BAND_PADDING_INNER + WATERFALL_BAND_PADDING_OUTER * 2);
    }, [chartWrapperWidth, processedData.length]);

    // Create connector line data
    const connectorData = useMemo(() => {
        if (!showConnectors) return [];

        const connectors: any[] = [];
        for (let i = 0; i < processedData.length - 1; i++) {
            const current = processedData[i];
            const next = processedData[i + 1];

            connectors.push({
                x: i + 0.45, // Position at end of current bar
                y: current.end,
                x1: i + 0.55, // Position at start of next bar
                y1: next.start,
            });
        }
        return connectors;
    }, [processedData, showConnectors]);

    // Render chart
    useEffect(() => {
        if (!chartContainer.current) return;

        // Destroy previous chart instance
        if (chartInstance.current) {
            chartInstance.current.destroy();
        }

        // Create new chart with top padding so labels above bars are not clipped
        const chart = new Chart({
            container: chartContainer.current,
            autoFit: true,
            paddingTop: 40, // Extra space so value/percentage labels above bars are not clipped
            paddingBottom: WATERFALL_X_AXIS_SIZE,
        });

        chart.coordinate({ type: 'cartesian' });

        // Add data
        chart.data(
            processedData.map((d, i) => ({
                ...d,
                index: i,
            })),
        );

        // Configure scales
        chart
            .scale('index', {
                type: 'band',
                paddingInner: 0.3,
                paddingOuter: 0.2,
            })
            .scale('y', { nice: true });

        // Add interval (bars) for the waterfall effect.
        // G2 v5 range intervals require field-name array channels (y/y1), not a callback returning [start, end].
        chart
            .interval()
            .encode('x', 'index')
            .encode('y', ['end', 'start'])
            .encode('color', (d: ProcessedWaterfallData) => {
                if (d.type === 'total') return 'total';
                if (d.type === 'increase') return 'increase';
                return 'decrease';
            })
            .scale('color', {
                type: 'ordinal',
                domain: ['increase', 'decrease', 'total'],
                range: [positiveColor, negativeColor, totalColor],
            })
            .style('radius', 2)
            .axis('x', {
                title: false,
                size: WATERFALL_X_AXIS_SIZE,
                labelAutoWrap: false,
                labelAutoHide: false,
                labelAutoRotate: false,
                labelFormatter: (text: string) => {
                    const idx = Number(text);
                    const category =
                        !Number.isNaN(idx) && processedData[idx] ? processedData[idx].category : text;
                    return formatFixedSpacingXAxisLabel(category, xAxisLabelStridePx);
                },
                labelSpacing: 4,
                labelFontSize: 13,
                labelFontFamily: CHART_FONT_FAMILY_CSS,
                labelStroke: '#000',
                labelOpacity: 0.4,
                labelFontWeight: 400,
            })
            .axis('y', {
                title: yAxisTitle,
                titleSpacing: 20,
                grid: true,
                gridStroke: 'rgba(0, 0, 0, 0.06)',
                gridLineWidth: 1,
                labelFontFamily: CHART_FONT_FAMILY_CSS,
                labelFormatter: (value: number) => {
                    const abs = Math.abs(value);
                    if (abs >= 1_000_000_000) {
                        return `${(value / 1_000_000_000).toFixed(2)}B`;
                    }
                    if (abs >= 1_000_000) {
                        return `${(value / 1_000_000).toFixed(2)}M`;
                    }
                    if (abs >= 1_000) {
                        return `${(value / 1_000).toFixed(2)}K`;
                    }
                    return value.toFixed(0);
                },
            })
            .legend(false)
            .tooltip({
                title: (d: any) => d.category,
                items: [
                    (d: ProcessedWaterfallData) => ({
                        name: 'Value',
                        value: d.value.toLocaleString(),
                    }),
                    (d: ProcessedWaterfallData) => ({
                        name: 'Range',
                        value: `${d.start.toLocaleString()} → ${d.end.toLocaleString()}`,
                    }),
                ],
            });

        // Add labels on top of bars
        if (showLabels) {
            chart
                .text()
                .encode('x', 'index')
                .encode('y', (d: ProcessedWaterfallData) => Math.max(d.start, d.end))
                .encode('text', (d: ProcessedWaterfallData) => {
                    const absValue = Math.abs(d.value);
                    let formattedValue = '';

                    if (absValue >= 1_000_000_000) {
                        formattedValue = `${(absValue / 1_000_000_000).toFixed(2)}B`;
                    } else if (absValue >= 1_000_000) {
                        formattedValue = `${(absValue / 1_000_000).toFixed(2)}M`;
                    } else if (absValue >= 1_000) {
                        formattedValue = `${(absValue / 1_000).toFixed(2)}K`;
                    } else {
                        formattedValue = absValue.toFixed(0);
                    }

                    return formattedValue;
                })
                .style('textAlign', 'center')
                .style('textBaseline', 'bottom')
                .style('dy', -5)
                .style('fontSize', 12)
                .style('fill', 'rgba(0, 0, 0, 0.65)')
                .style('fontFamily', CHART_FONT_FAMILY_CSS);

            // Add percentage labels if enabled
            if (showPercentages) {
                chart
                    .text()
                    .encode('x', 'index')
                    .encode('y', (d: ProcessedWaterfallData) => Math.max(d.start, d.end))
                    .encode('text', (d: ProcessedWaterfallData) => {
                        if (d.type === 'total') {
                            return '';
                        }
                        if (d.percentage && d.percentage > 0.01) {
                            return `${d.percentage.toFixed(2)}%`;
                        }
                        return '';
                    })
                    .style('textAlign', 'center')
                    .style('textBaseline', 'bottom')
                    .style('dy', -20)
                    .style('fontSize', 11)
                    .style('fill', 'rgba(0, 0, 0, 0.45)')
                    .style('fontFamily', CHART_FONT_FAMILY_CSS);
            }
        }

        // Add connector lines
        if (showConnectors && connectorData.length > 0) {
            connectorData.forEach((connector) => {
                chart
                    .line()
                    .data([
                        { x: connector.x, y: connector.y },
                        { x: connector.x1, y: connector.y1 },
                    ])
                    .encode('x', 'x')
                    .encode('y', 'y')
                    .style('stroke', connectorColor)
                    .style('strokeWidth', 1)
                    .style('lineDash', [4, 4])
                    .tooltip(false);
            });
        }

        // Handle bar clicks
        if (onBarClick) {
            chart.on('interval:click', (event: any) => {
                onBarClick(event.data?.data);
            });
        }

        chart.render();
        chartInstance.current = chart;

        return () => {
            chart.destroy();
        };
    }, [
        processedData,
        connectorData,
        height,
        positiveColor,
        negativeColor,
        totalColor,
        connectorColor,
        showLabels,
        showConnectors,
        showPercentages,
        yAxisTitle,
        onBarClick,
        xAxisLabelStridePx,
    ]);

    return (
        <ConfigProvider theme={theme}>
            <div
                className={`w-full bg-white rounded-lg border border-[rgba(0,0,0,0.06)] ${className}`}
                data-test={props['data-test']}
                data-test-id={props['data-test-id']}
                data-asper-waterfall-chart
            >
                {/* Header Section */}
                <div className="p-5 border-b border-[rgba(0,0,0,0.06)]">
                    <div className="flex justify-between items-start mb-2">
                        <h3 className="font-['Open_Sans'] font-semibold text-[16px] leading-normal text-[rgba(0,0,0,0.88)] m-0">
                            {chartTitle}
                        </h3>
                        {onDownload && (
                            <Button
                                type="text"
                                icon={<DownloadFile size={16} />}
                                onClick={onDownload}
                                className="!p-2"
                                data-test-id={`${props['data-test-id']}-download-btn`}
                            />
                        )}
                    </div>

                    {description && (
                        <p className="font-['Open_Sans'] font-normal text-[14px] leading-[22px] text-[rgba(0,0,0,0.65)] m-0">
                            {description}
                        </p>
                    )}
                </div>

                {/* Filters Section */}
                {(showMetricDropdown || showDimensionDropdown || filters.length > 0) && (
                    <div className="px-5 pt-5 pb-3">
                        <div className="flex flex-wrap gap-4">
                            {showMetricDropdown && (
                                <div className="flex flex-col gap-1">
                                    <label className="font-['Open_Sans'] font-normal text-sm leading-[20px] text-[rgba(0,0,0,0.45)]">
                                        Metric
                                    </label>
                                    <Select
                                        value={selectedMetricIndex}
                                        onChange={(value) => setSelectedMetricIndex(Number(value))}
                                        options={metricOptions}
                                        className="min-w-[180px]"
                                        data-test-id={`${props['data-test-id']}-metric-filter`}
                                    />
                                </div>
                            )}
                            {showDimensionDropdown && (
                                <div className="flex flex-col gap-1">
                                    <label className="font-['Open_Sans'] font-normal text-sm leading-[20px] text-[rgba(0,0,0,0.45)]">
                                        Breakdown
                                    </label>
                                    <Select
                                        value={selectedDimension}
                                        onChange={(value) => setSelectedDimension(value as WaterfallDimension)}
                                        options={dimensionOptions.map((option) => ({
                                            label: option,
                                            value: option,
                                        }))}
                                        className="min-w-[180px]"
                                        data-test-id={`${props['data-test-id']}-dimension-filter`}
                                    />
                                </div>
                            )}
                            {filters.map((filter, index) => (
                                <div key={index} className="flex flex-col gap-1">
                                    <label className="font-['Open_Sans'] font-normal text-sm leading-[20px] text-[rgba(0,0,0,0.45)]">
                                        {filter.label}
                                    </label>
                                    <Select
                                        value={filter.defaultValue}
                                        onChange={filter.onChange}
                                        options={filter.options}
                                        className="min-w-[180px]"
                                        data-test-id={`${props['data-test-id']}-filter-${index}`}
                                    />
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Y-Axis Label */}
                {yAxisTitle && (
                    <div className="px-5 pt-3">
                        <p className="font-['Open_Sans'] font-normal text-sm leading-[20px] text-[rgba(0,0,0,0.45)] m-0">
                            {yAxisTitle}
                        </p>
                    </div>
                )}

                {/* Chart Section */}
                <div className={`px-5 py-5 ${chartClassName}`} style={{ height: `${height}px` }}>
                    <div ref={chartContainer} style={{ width: '100%', height: '100%' }} />
                </div>

                {/* Footer Section */}
                {footerLabel && (
                    <div className="px-5 pb-5 flex justify-center items-center gap-2">
                        <span className="font-['Open_Sans'] font-semibold text-[14px] leading-[22px] text-[rgba(0,0,0,0.88)]">
                            {footerLabel}
                        </span>
                        {showFooterArrow && <ArrowRight size={16} color="rgba(0,0,0,0.88)" />}
                    </div>
                )}
            </div>
        </ConfigProvider>
    );
};

export default WaterfallChart;
